import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * Modal that sends a structured quote reply to a quote_requests record.
 * POSTs to /admin/quotes/{ref}/reply which:
 *  - emails the requester via Resend
 *  - marks the quote as 'contacted'
 *  - stores the reply payload in the audit log
 *
 * Extracted from AdminDesk so it can be reused from the dashboard drill-down.
 */
export default function QuoteReplyModal({ quote, onClose, onSent }) {
  const initialPrice = quote.reply?.price_per_ton_usd ?? quote.unit_price_usd ?? 0;
  const [pricePerTon, setPricePerTon] = useState(initialPrice);
  const [totalUsd, setTotalUsd] = useState(quote.reply?.total_usd ?? Number(quote.tons || 0) * initialPrice);
  const [expiry, setExpiry] = useState(quote.reply?.expiry_date ?? "");
  const [rail, setRail] = useState(quote.reply?.payment_rail ?? quote.settlement_method ?? "swift");
  const [notes, setNotes] = useState(quote.reply?.notes ?? "");
  const [busy, setBusy] = useState(false);

  // Auto-compute total when price changes (user can still override).
  useEffect(() => {
    const computed = Number(quote.tons || 0) * Number(pricePerTon || 0);
    if (Number.isFinite(computed)) setTotalUsd(Number(computed.toFixed(2)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricePerTon]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/admin/quotes/${quote.ref}/reply`, {
        price_per_ton_usd: Number(pricePerTon),
        total_usd: Number(totalUsd),
        expiry_date: expiry,
        payment_rail: rail,
        notes: notes || null,
      });
      toast.success(`Quote ${quote.ref} sent to ${quote.contact_email}`);
      onSent?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-8 bg-ink/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="reply-overlay"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="relative w-full max-w-xl bg-bg border border-ink shadow-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="border-b border-line px-6 py-4 flex items-center justify-between sticky top-0 bg-bg">
          <div>
            <div className="label-tag">Send structured quote</div>
            <div className="font-serif text-xl mt-1">{quote.ref}</div>
            <div className="text-[11px] font-mono text-ink2 mt-1">
              {quote.company_name} · {quote.contact_email}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            className="text-ink2 hover:text-ink text-2xl leading-none px-2">×</button>
        </div>

        <div className="px-6 py-5 space-y-4 text-sm">
          <div className="border border-line bg-surface p-3 text-xs text-ink2 font-mono">
            Project: <span className="text-ink">{quote.project_name || "—"}</span> ·
            Tons: <span className="text-ink">{Number(quote.tons || 0).toLocaleString()}</span> ·
            Original total: <span className="text-ink">${Number(quote.total_usd || 0).toLocaleString()}</span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Price / ton (USD)" testid="reply-price" type="number" step="0.01" min="0.01"
              required value={pricePerTon} onChange={setPricePerTon} />
            <Field label="Total (USD)" testid="reply-total" type="number" step="0.01" min="0.01"
              required value={totalUsd} onChange={setTotalUsd} />
            <Field label="Valid until" testid="reply-expiry" type="date" required
              value={expiry} onChange={setExpiry} />
            <div>
              <label className="label-tag block mb-1">Payment rail</label>
              <select data-testid="reply-rail" value={rail} onChange={(e) => setRail(e.target.value)}
                className="input-field !py-2">
                <option value="swift">SWIFT bank wire</option>
                <option value="usdt">USDT (TRC-20)</option>
                <option value="stripe">Stripe (card)</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div>
            <label className="label-tag block mb-1">Notes (optional, appears in email)</label>
            <textarea data-testid="reply-notes" rows={4} value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Includes settlement instructions in the attached PDF…"
              className="input-field !py-2" />
          </div>

          <div className="text-[11px] font-mono text-ink2 leading-relaxed">
            Sending will email <span className="text-ink">{quote.contact_email}</span> via Resend with the
            structured quote, mark this request as <span className="text-ink">contacted</span>, and store
            the reply in the audit log.
          </div>
        </div>

        <div className="border-t border-line px-6 py-3 flex justify-end gap-2 sticky bottom-0 bg-bg">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-xs font-mono uppercase tracking-wider border border-line text-ink2 hover:text-ink">
            Cancel
          </button>
          <button type="submit" disabled={busy} data-testid="reply-send"
            className="btn-primary !text-xs !px-5 !py-2 disabled:opacity-50">
            {busy ? "Sending…" : "Send quote →"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, testid, value, onChange, ...rest }) {
  return (
    <div>
      <label className="label-tag block mb-1">{label}</label>
      <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="input-field !py-2" {...rest} />
    </div>
  );
}
